import { isArray } from '@/utils/is';
import { deepMerge } from '@/utils';

// 组件类型映射
const ELEMENT_TYPE_MAP = {
  INPUT: 'input',
  SELECT: 'select',
  CASCADER: 'cascader',
  RADIO_GROUP: 'radio-group',
  CHECKBOX_GROUP: 'checkbox-group',
  IMAGE: 'image',
};

// 值类型为数组
const array_value = ['FIELD_TYPE_STRING_ARRAY'];

// 初始值策略
const value_strategy = {
  [ELEMENT_TYPE_MAP.INPUT]() {
    return '';
  },
  [ELEMENT_TYPE_MAP.SELECT](config) {
    const { field_type } = config;
    return array_value.includes(field_type) ? [] : '';
  },
  [ELEMENT_TYPE_MAP.CASCADER](config) {
    const { field_type } = config;
    return array_value.includes(field_type) ? [] : '';
  },
  [ELEMENT_TYPE_MAP.RADIO_GROUP]() {
    return '';
  },
  [ELEMENT_TYPE_MAP.CHECKBOX_GROUP]() {
    return [];
  },
  [ELEMENT_TYPE_MAP.IMAGE]() {
    return '';
  },
};

// 属性策略
const properties_strategy = {
  [ELEMENT_TYPE_MAP.INPUT](config) {
    const { restriction, description } = config;
    const properties = restriction?.text_restriction;
    const map_properties = {
      min_length: 'minlength',
      max_length: 'maxlength',
    };
    const init_props = { showWordLimit: true };
    const hasOwnProperty = (target, property) => Object.prototype.hasOwnProperty.call(target || {}, property);
    const rules = (hasOwnProperty(properties, 'min_length') && hasOwnProperty(properties, 'max_length')) ? [{
      required: true,
      message: `${description}限制${properties.min_length} - ${properties.max_length}个字`,
      trigger: ['change'],
      validator: (rule, value, callback) => {
        if (value?.length >= properties.min_length && value?.length <= properties.max_length) {
          callback();
        } else {
          callback(new Error(rule.message));
        }
      },
    }] : [];
    return {
      props: getMergeProperties({
        map_properties,
        init_props,
        properties,
      }),
      rules,
    };
  },
  [ELEMENT_TYPE_MAP.SELECT](config) {
    const { field_type, array_property } = config;
    const map_properties = {
      max_number: 'multipleLimit',
    };
    const init_props = {
      multiple: array_value.includes(field_type),
      clearable: true,
      collapseTags: true,
      filterable: true,
    };
    return {
      props: getMergeProperties({
        map_properties,
        init_props,
        properties: array_property,
      }),
    };
  },
  [ELEMENT_TYPE_MAP.CASCADER](config) {
    const { field_type } = config;
    return {
      props: {
        props: {
          emitPath: array_value.includes(field_type),
        },
      },
    };
  },
  [ELEMENT_TYPE_MAP.RADIO_GROUP]() {
    return {};
  },
  [ELEMENT_TYPE_MAP.CHECKBOX_GROUP]() {
    return {};
  },
  [ELEMENT_TYPE_MAP.IMAGE](config) {
    const { restriction } = config;
    const properties = restriction?.image_restriction;
    const map_properties = {
      width: 'width',
      height: 'height',
    };
    const init_props = {};
    return {
      material_restriction: [getMergeProperties({ map_properties, init_props, properties })],
    };
  },
};

/**
 * 生成组件基础骨架
 * @param {Object} config 接口数据配置
 * @returns {Object}
 */
function generateComponentSkeleton(config) {
  const { name, description, element_type, required } = config;
  const map_placeholder = { [ELEMENT_TYPE_MAP.INPUT]: '请输入', [ELEMENT_TYPE_MAP.SELECT]: '请选择', [ELEMENT_TYPE_MAP.CASCADER]: '请选择' };
  const message = `${map_placeholder[element_type] || map_placeholder[ELEMENT_TYPE_MAP.SELECT]}${description}`;
  return {
    [name]: {
      label: description,
      type: element_type,
      rules: [
        {
          required,
          message,
          trigger: ['change'],
        },
      ],
      props: {
        placeholder: message,
      },
    },
  };
}

/**
 * 获取合并属性
 * @param {Object} config 配置数据
 * @param {Object} config.init_props 初始属性
 * @param {Object} config.map_properties 映射属性（本地属性名称：接口属性名称）
 * @param {Object} config.properties 接口属性数据
 * @returns {Object}
 */
function getMergeProperties(config) {
  const { properties = {}, map_properties = {}, init_props = {}} = config || {};
  const ret = Object.keys(properties).reduce((pre, acc) => {
    const property = map_properties[acc];
    return Object.assign(pre, {
      ...(property && { [property]: properties[acc] }),
    });
  }, init_props);
  return ret;
}

/**
 * 生成组件所有的属性
 * @param {Object} config 接口数据配置
 * @returns {Object}
 */
function generateComponentProperties(config) {
  const { name } = config;
  const { element_type, default_value } = config;
  const component_properties = {
    [name]: {
      ...properties_strategy[element_type]?.(config) || {},
      value: default_value || value_strategy[element_type]?.(config) || '',
    },
  };
  return deepMerge([component_properties, generateComponentSkeleton(config)]);
}

/**
 * 生成动态表单配置
 * @param {Array} data 动态组件数据
 * @returns {Object}
 */
export function generateDynamicFormModel(data) {
  if (!isArray(data) || data.length === 0) {
    return {};
  }
  const model = data.reduce((pre, acc) => Object.assign(pre, generateComponentProperties(acc)), {});
  return model;
}

/**
 * 根据节点收集父节点和自身节点的label数据
 * @param {Object[]} data 树状结构的数组
 * @param {Object} target 目标节点，要包含value和label属性
 * @returns {Array | null}
 */
export function collectParentAndSelfLabels(data, target) {
  const result = [];
  const handler = (items) => {
    for (const item of items) {
      if (item.value === target.value) {
        result.unshift(item.label);
        return result;
      }
      if (item.children && item.children.length > 0) {
        // 尝试在子树中查找
        const found = handler(item.children);
        if (found) {
          // 如果在子树中找到目标，添加当前节点的label到结果数组
          result.unshift(item.label);
          // 返回找到的结果
          return found;
        }
      }
    }
    return null;
  };
  const found = handler(data);
  return found || null;
}

/**
 * 根据配置生成html内容
 * @param {Object} config 配置
 * @param {Array<string>} config.content 内容
 * @param {boolean} config.is_index 是否显示序号
 * @param {string} config.el 实际渲染的元素标签
 */
export function generateHtmlContent({ content, is_index = true, el = 'p' }){
  return content.reduce((prev, cur, index) => {
    return prev + `<${el}>${is_index ? `${index + 1}. ` : ''}${cur}</${el}>`;
  }, '');
}
